'use client';

import { HeroCardBody } from './HeroCardBody';
import { HeroCardPanel } from './HeroCardPanel';
import { HeroCardPhoneLink } from './HeroCardPhoneLink';
import { HeroCardSearch } from './HeroCardSearch';
import { resolveHeroCardPrices } from './resolveHeroCard';
import type { HeroCardPricesConfig, HeroCardRootProps } from './types';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { Text } from '@/lib/render/Text';

export interface HeroCardPricesProps extends HeroCardRootProps {
  config: HeroCardPricesConfig;
}

/**
 * The Prices page card: a left-aligned title, a rich-text body carrying the
 * intro and the lead-in line, and the PLZ/Ort finder with its own submit
 * button.
 *
 * Below the finder sits an optional call CTA — a lead-in line ("Wir helfen
 * Ihnen gerne telefonisch:") above an icon-led phone link, the same
 * `.link-phone` visual the tabbed cards' post-CTA block uses. It renders only
 * when a number resolves, so a card without one keeps no trailing gap.
 */
export function HeroCardPrices({ config, ...rest }: HeroCardPricesProps) {
  const resolved = resolveHeroCardPrices(config);
  const { substitutePlaceholders: sub } = useRenderContext();

  // The call CTA is opt-in: filling the number is the signal. There is no
  // implicit fallback — an editor who wants the brand's number types
  // `{domain.phone}`, and the label takes `{domain.matelsoPhoneLabel}` or any
  // literal. A chain here would hand every card a number and render the CTA on
  // every Prices card, which is also why this widget is deliberately not one of
  // the backend's PHONE_WIDGET_TYPES.
  const phone = resolved.phone ? sub(resolved.phone) : '';
  const phoneLabel = resolved.matelsoPhoneLabel
    ? sub(resolved.matelsoPhoneLabel)
    : '';

  return (
    <HeroCardPanel {...rest}>
      {resolved.title && (
        <Text
          as="h2"
          field="title"
          className="text-xl leading-tight font-bold tracking-tight lg:text-2xl"
        >
          {resolved.title}
        </Text>
      )}
      <HeroCardBody nodes={resolved.body} />
      <HeroCardSearch search={resolved.search} />
      {phone && (
        <div className="flex w-full flex-col gap-2">
          {resolved.callCtaText && (
            <Text
              as="p"
              field="callCtaText"
              className="m-0 text-center text-sm"
            >
              {resolved.callCtaText}
            </Text>
          )}
          <HeroCardPhoneLink phone={phone} label={phoneLabel} />
        </div>
      )}
    </HeroCardPanel>
  );
}
